import React, { useEffect, useMemo, useState } from 'react';
import {
    Box, Button, IconButton, Menu, MenuItem, Paper, Table, TableBody, TableCell, TableContainer, TableHead,
    TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { useTranslation } from 'react-i18next';
import { AppraisalService } from '../../services/appraisal.service';
import { useToast } from '../../context/ToastContext';
import { ICriterionRequest, ICycleDetail, ICycleSummary, MAX_CRITERIA } from '../../interfaces/appraisal.interface';
import ConfirmDialog from './ConfirmDialog';

interface Row {
    key: string;
    name: string;
    description: string;
    weight: string;
}

interface Props {
    institutionId: string;
    cycle: ICycleDetail;
    onSaved: (cycle: ICycleDetail) => void;
    onDirtyChange: (dirty: boolean) => void;
}

let rowSeq = 0;
const newKey = () => `new-${++rowSeq}`;

const rowsFrom = (cycle: ICycleDetail): Row[] =>
    cycle.criteria.slice().sort((a, b) => a.sortOrder - b.sortOrder).map(c => ({
        key: c.id, name: c.name, description: c.description ?? '', weight: String(c.weight),
    }));

const weightOf = (row: Row) => {
    const n = Number(row.weight);
    return Number.isFinite(n) ? n : 0;
};

const sameRows = (a: Row[], b: Row[]) =>
    a.length === b.length && a.every((r, i) =>
        r.name === b[i].name && r.description === b[i].description && weightOf(r) === weightOf(b[i]));

/** The cycle's criteria. Editable while the cycle is a draft; the whole list is saved in one call. */
const CriteriaPanel: React.FC<Props> = ({ institutionId, cycle, onSaved, onDirtyChange }) => {
    const toast = useToast();
    const { t } = useTranslation(['appraisal', 'common']);
    const editable = cycle.status === 'DRAFT';
    // Keyed on the criteria themselves, so reloading the cycle for other reasons keeps unsaved edits.
    const criteriaKey = JSON.stringify(cycle.criteria);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const saved = useMemo(() => rowsFrom(cycle), [criteriaKey]);
    const [rows, setRows] = useState<Row[]>(saved);
    const [busy, setBusy] = useState(false);
    const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
    const [otherCycles, setOtherCycles] = useState<ICycleSummary[]>([]);
    const [copySource, setCopySource] = useState<ICycleSummary | null>(null);

    useEffect(() => setRows(saved), [saved]);

    const dirty = !sameRows(rows, saved);
    useEffect(() => onDirtyChange(dirty), [dirty, onDirtyChange]);

    const total = rows.reduce((sum, r) => sum + weightOf(r), 0);

    const update = (index: number, patch: Partial<Row>) =>
        setRows(rs => rs.map((r, i) => (i === index ? { ...r, ...patch } : r)));
    const move = (index: number, delta: number) =>
        setRows(rs => {
            const next = rs.slice();
            const [row] = next.splice(index, 1);
            next.splice(index + delta, 0, row);
            return next;
        });

    const save = async () => {
        if (rows.length < 1 || rows.length > MAX_CRITERIA) {
            toast.showError(t('criteria.errors.count', { max: MAX_CRITERIA }));
            return;
        }
        if (rows.some(r => !r.name.trim())) {
            toast.showError(t('criteria.errors.nameRequired'));
            return;
        }
        if (rows.some(r => !/^\d+$/.test(r.weight.trim()) || weightOf(r) < 1 || weightOf(r) > 100)) {
            toast.showError(t('criteria.errors.weightRange'));
            return;
        }
        const body: ICriterionRequest[] = rows.map(r => ({
            name: r.name.trim(),
            description: r.description.trim() || undefined,
            weight: weightOf(r),
        }));
        setBusy(true);
        try {
            onSaved(await AppraisalService.replaceCriteria(institutionId, cycle.id, body));
            toast.showSuccess(t('criteria.saved'));
        } catch (err) {
            toast.showApiError(err, t('criteria.errors.save'));
        } finally {
            setBusy(false);
        }
    };

    const openCopyMenu = async (anchor: HTMLElement) => {
        setMenuAnchor(anchor);
        try {
            const all = await AppraisalService.listCycles(institutionId);
            setOtherCycles(all.filter(c => c.id !== cycle.id));
        } catch (err) {
            toast.showApiError(err, t('errors.loadCycles'));
        }
    };

    const copy = async () => {
        if (!copySource) return;
        setBusy(true);
        try {
            onSaved(await AppraisalService.copyCriteria(institutionId, cycle.id, copySource.id));
            setCopySource(null);
            toast.showSuccess(t('criteria.copied'));
        } catch (err) {
            toast.showApiError(err, t('criteria.errors.copy'));
        } finally {
            setBusy(false);
        }
    };

    const rowLabel = (row: Row, index: number) => row.name.trim() || t('criteria.numbered', { number: index + 1 });

    return (
        <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{t('criteria.intro')}</Typography>
            <TableContainer component={Paper}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ width: '30%' }}>{t('criteria.name')}</TableCell>
                            <TableCell>{t('criteria.description')}</TableCell>
                            <TableCell sx={{ width: 120 }}>{t('criteria.weight')}</TableCell>
                            {editable && <TableCell sx={{ width: 140 }} />}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rows.length === 0 && (
                            <TableRow><TableCell colSpan={4}><Typography color="text.secondary">{t('criteria.empty')}</Typography></TableCell></TableRow>
                        )}
                        {rows.map((row, i) => editable ? (
                            <TableRow key={row.key}>
                                <TableCell>
                                    <TextField size="small" fullWidth value={row.name} onChange={e => update(i, { name: e.target.value })}
                                        inputProps={{ maxLength: 120, 'aria-label': t('criteria.nameFor', { number: i + 1 }) }} />
                                </TableCell>
                                <TableCell>
                                    <TextField size="small" fullWidth multiline value={row.description}
                                        onChange={e => update(i, { description: e.target.value })}
                                        inputProps={{ maxLength: 1000, 'aria-label': t('criteria.descriptionFor', { name: rowLabel(row, i) }) }} />
                                </TableCell>
                                <TableCell>
                                    <TextField size="small" type="number" value={row.weight}
                                        onChange={e => update(i, { weight: e.target.value })}
                                        inputProps={{ min: 1, max: 100, step: 1, 'aria-label': t('criteria.weightFor', { name: rowLabel(row, i) }) }} />
                                </TableCell>
                                <TableCell>
                                    <Tooltip title={t('criteria.moveUp')}>
                                        <span><IconButton size="small" disabled={i === 0} onClick={() => move(i, -1)} aria-label={t('criteria.moveUp')}><ArrowUpwardIcon fontSize="small" /></IconButton></span>
                                    </Tooltip>
                                    <Tooltip title={t('criteria.moveDown')}>
                                        <span><IconButton size="small" disabled={i === rows.length - 1} onClick={() => move(i, 1)} aria-label={t('criteria.moveDown')}><ArrowDownwardIcon fontSize="small" /></IconButton></span>
                                    </Tooltip>
                                    <Tooltip title={t('criteria.remove')}>
                                        <IconButton size="small" color="error" onClick={() => setRows(rs => rs.filter((_, j) => j !== i))} aria-label={t('criteria.remove')}>
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                </TableCell>
                            </TableRow>
                        ) : (
                            <TableRow key={row.key}>
                                <TableCell>{row.name}</TableCell>
                                <TableCell sx={{ whiteSpace: 'pre-wrap' }}>{row.description || '—'}</TableCell>
                                <TableCell>{t('criteria.percent', { value: row.weight })}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2, flexWrap: 'wrap' }}>
                <Typography fontWeight={600} color={total === 100 ? 'success.main' : 'error.main'}>
                    {t('criteria.total', { total })}
                </Typography>
                {editable && (
                    <>
                        <Box sx={{ flex: 1 }} />
                        <Button onClick={e => openCopyMenu(e.currentTarget)} disabled={busy}>{t('criteria.copyFrom')}</Button>
                        <Button
                            variant="outlined" disabled={busy || rows.length >= MAX_CRITERIA}
                            onClick={() => setRows(rs => [...rs, { key: newKey(), name: '', description: '', weight: '' }])}
                        >
                            {t('criteria.add')}
                        </Button>
                        <Button variant="contained" onClick={save} disabled={busy || !dirty}>{t('criteria.save')}</Button>
                    </>
                )}
            </Box>
            {editable && rows.length >= MAX_CRITERIA && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>{t('criteria.max', { max: MAX_CRITERIA })}</Typography>
            )}

            <Menu anchorEl={menuAnchor} open={!!menuAnchor} onClose={() => setMenuAnchor(null)}>
                {otherCycles.length === 0 ? (
                    <MenuItem disabled>{t('criteria.noOtherCycles')}</MenuItem>
                ) : otherCycles.map(c => (
                    <MenuItem key={c.id} onClick={() => { setMenuAnchor(null); setCopySource(c); }}>{c.name}</MenuItem>
                ))}
            </Menu>

            <ConfirmDialog
                open={!!copySource}
                title={t('criteria.confirmCopy.title')}
                body={t('criteria.confirmCopy.body', { name: copySource?.name ?? '' })}
                confirmLabel={t('criteria.confirmCopy.confirm')}
                busy={busy}
                onConfirm={copy}
                onClose={() => setCopySource(null)}
            />
        </Box>
    );
};

export default CriteriaPanel;
